/**
 * Domain entities shared by the API, web, and (via codegen) native clients.
 * Wire format = JSON. Timestamps are epoch milliseconds. IDs are ULIDs (lexicographically time-ordered).
 * Optional fields are omitted (never null) on the wire.
 */
import { z } from "zod";
import { KitBlock } from "./blockKit.ts";

export const Id = z.string().min(1).max(64);
export const Timestamp = z.number().int().nonnegative();
export const EmailAddress = z.string().min(3).max(320);

export const Role = z.enum(["owner", "admin", "member", "guest"]);
export type Role = z.infer<typeof Role>;

// ---------- Scoped access (guests from other companies AND agents share one model) ----------

export const Organization = z.object({ name: z.string(), domain: z.string().optional() });
export type Organization = z.infer<typeof Organization>;

export const DmPolicy = z.enum(["anyone", "shared_channels", "sponsor_only", "none"]);
export type DmPolicy = z.infer<typeof DmPolicy>;
export const DirectoryPolicy = z.enum(["all", "shared_channels"]);
export type DirectoryPolicy = z.infer<typeof DirectoryPolicy>;

export const Capabilities = z.object({
  dm: DmPolicy,
  seeDirectory: DirectoryPolicy,
  createChannels: z.boolean(),
  invite: z.boolean(),
  /** Search stays scoped to visible content either way; false hides search entirely. */
  search: z.boolean(),
  downloadFiles: z.boolean(),
  joinHuddles: z.boolean(),
  /** May use @here / @channel / @everyone. */
  mentionBroadcast: z.boolean(),
  seeEmails: z.boolean(),
  /**
   * May create stickers and sticker packs (stickers.ts). Absent ⇒ restricted members can't (they can still send
   * stickers and GIFs); full members follow the workspace's `MediaSettings.stickerCreators`.
   */
  createStickers: z.boolean().optional(),
});
export type Capabilities = z.infer<typeof Capabilities>;

/**
 * Access policy for a member. Absent ⇒ normal access for their role.
 * "restricted" members (guests, and agents by default) see ONLY: channels they were added to, pages/databases
 * explicitly shared (`pageIds` subtrees), and people allowed by `can.seeDirectory`. Enforced server-side in
 * bootstrap, history, search, realtime fan-out and every mutation.
 */
export const AccessPolicy = z.object({
  level: z.enum(["full", "restricted"]),
  /** Internal member accountable for this guest/agent: approves scope changes, receives the audit trail. */
  sponsorId: Id.optional(),
  /** External company → "Guest · <name>" badge everywhere; channels containing externals are marked. */
  organization: Organization.optional(),
  /** Access ends automatically (sessions revoked, memberships frozen, content kept). */
  expiresAt: Timestamp.optional(),
  /** Explicitly shared page/database subtrees (restricted only). Channels are governed by channel membership. */
  pageIds: z.array(Id).optional(),
  /** Cap on channel memberships (1 = single-channel guest). */
  maxChannels: z.number().int().optional(),
  can: Capabilities,
});
export type AccessPolicy = z.infer<typeof AccessPolicy>;

export const Locale = z.enum(["en", "pt-BR"]);
export type Locale = z.infer<typeof Locale>;

/**
 * The product areas an owner or admin can switch on or off for the whole workspace (core `logic/features`), like
 * Google Workspace's per-service on/off and Slack's admin feature settings. Turning one off hides it in every client
 * and its API routes answer 403 `feature_disabled`; nothing is deleted, and turning it back on restores everything.
 * `calendar` lives in the Email view, so it also needs `mail`; `huddles` is an area of its own (it works with Chat off).
 */
export const FeatureArea = z.enum(["chat", "mail", "calendar", "contacts", "docs", "huddles"]);
export type FeatureArea = z.infer<typeof FeatureArea>;

/** Per-area switches. A missing key means on (new areas and old workspaces stay enabled). */
export const WorkspaceFeatures = z.object({
  chat: z.boolean().optional(),
  mail: z.boolean().optional(),
  calendar: z.boolean().optional(),
  contacts: z.boolean().optional(),
  docs: z.boolean().optional(),
  huddles: z.boolean().optional(),
});
export type WorkspaceFeatures = z.infer<typeof WorkspaceFeatures>;

/**
 * What owners and admins keep of an area that is switched off (the admin's choice): `none` — hidden and blocked for
 * them too; `read` — they still see it and its content but can't change anything; `full` — they keep using it (to
 * set it up before turning it on for everyone).
 */
export const FeatureAdminAccess = z.enum(["none", "read", "full"]);
export type FeatureAdminAccess = z.infer<typeof FeatureAdminAccess>;

/**
 * Who can join a huddle link: "ask" = people outside the workspace ask and someone in the call lets them in (members
 * join directly); "open" = anyone with the link joins directly; "workspace" = only signed-in members of the workspace.
 */
export const MeetingPolicy = z.enum(["ask", "open", "workspace"]);
export type MeetingPolicy = z.infer<typeof MeetingPolicy>;

/**
 * Settings → Huddles (admins): links to share and people outside the workspace (core `logic/huddlesArea`). Absent on
 * the workspace ⇒ `{ links: true, outside: "link", defaultPolicy: "ask", transcribe: true }`.
 */
export const HuddleLinkSettings = z.object({
  /** Members can make links to share. Off: no new links, and existing links only let members in. */
  links: z.boolean(),
  /** People outside the workspace: "link" = as each link says; "ask" = they always ask to join; "none" = they can't join. */
  outside: z.enum(["link", "ask", "none"]),
  /** "Who can join" for new links. */
  defaultPolicy: MeetingPolicy,
  /**
   * Transcribe huddles: every huddle is transcribed while people are unmuted (never a muted mic) and the transcript is
   * saved for everyone in it. Off: captions only for whoever turns them on, not saved. Absent ⇒ on (core
   * `huddleLinkSettings` always fills it in).
   */
  transcribe: z.boolean().optional(),
});
export type HuddleLinkSettings = z.infer<typeof HuddleLinkSettings>;

export const Workspace = z.object({
  id: Id,
  slug: z.string(),
  name: z.string(),
  /** API-relative image URL (a `workspace_icon` upload, files.ts): media pipeline, readable by every member. */
  iconUrl: z.string().optional(),
  createdAt: Timestamp,
  /** Language for new members and server-rendered text when a member has none (settings.ts). Absent ⇒ "en". */
  defaultLocale: Locale.optional(),
  /**
   * The primary owner (the creator until ownership is transferred): always an owner, can't be demoted or
   * deactivated, and must transfer primary ownership before leaving or deleting their account (core `logic/roles`).
   */
  primaryOwnerId: Id.optional(),
  /** AI thread summaries (admins; docs/features/thread-summaries.md). Absent ⇒ on. */
  aiThreadSummaries: z.boolean().optional(),
  /** Link previews under messages (Slack "Show link previews"; admins). Absent ⇒ on. */
  unfurlLinks: z.boolean().optional(),
  /**
   * Members may add "Sent with Bundle" to their emails (admins; like Outlook's org switch for "Get Outlook for…").
   * Off leaves it out of every message and locks members' setting off. Absent ⇒ allowed.
   */
  mailSentWith: z.boolean().optional(),
  /** Product areas switched off by an admin (only `false` entries are sent). Absent ⇒ everything on. */
  features: WorkspaceFeatures.optional(),
  /** What owners and admins keep of switched-off areas. Absent ⇒ `none`. */
  featureAdminAccess: FeatureAdminAccess.optional(),
  /** Settings → Huddles (links to share, people outside). Absent ⇒ the defaults (see HuddleLinkSettings). */
  huddleLinks: HuddleLinkSettings.optional(),
});
export type Workspace =z.infer<typeof Workspace>;

/** Avatar background tone 1–8: the `avatar.1`…`avatar.8` colour tokens (light/dark pairs). */
export const AvatarTone = z.number().int().min(1).max(8);

/**
 * How a member looks: a photo (an `avatar` upload, files.ts; API-relative URL through the media pipeline), their
 * initials on a chosen tone, or an emoji on a tone. Absent ⇒ initials on the tone derived from the name (core
 * `Format.avatarTone`).
 */
export const MemberAvatar = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("photo"), url: z.string() }),
  z.object({ kind: z.literal("initials"), color: AvatarTone }),
  z.object({ kind: z.literal("emoji"), emoji: z.string().min(1).max(32), color: AvatarTone }),
]);
export type MemberAvatar = z.infer<typeof MemberAvatar>;

export const UserStatus = z.object({
  emoji: z.string(),
  text: z.string(),
  expiresAt: Timestamp.optional(),
});
export type UserStatus = z.infer<typeof UserStatus>;

/**
 * A daily quiet-hours window in the person's time zone: from `start` to `end` (minutes after midnight; `end` <=
 * `start` runs past midnight) on the days it starts (0 = Sunday … 6 = Saturday). Core `logic/dnd`.
 */
export const DndSchedule = z.object({
  days: z.array(z.number().int().min(0).max(6)).max(7),
  start: z.number().int().min(0).max(1439),
  end: z.number().int().min(0).max(1439),
  timeZone: z.string().min(1).max(64),
});
export type DndSchedule = z.infer<typeof DndSchedule>;

/**
 * A member's Do not disturb as coworkers see it (a copy of their NotificationSettings, kept in every workspace they're
 * in): clients show a moon / "Notifications paused" while core `Dnd.isPaused(now, dnd)` says so. Absent ⇒ never paused.
 */
export const MemberDnd = z.object({
  pausedUntil: Timestamp.optional(),
  schedule: DndSchedule.optional(),
});
export type MemberDnd = z.infer<typeof MemberDnd>;

/** Agents are members, not bots: same identity, memberships, permissions and audit trail as people. */
export const MemberKind = z.enum(["human", "agent"]);
export type MemberKind = z.infer<typeof MemberKind>;

export const AgentState = z.enum(["idle", "working", "waiting", "error", "offline"]);
export type AgentState = z.infer<typeof AgentState>;

export const AgentProfile = z.object({
  /** "hosted" = runs in Bundle (AgentDO + Claude API, workspace BYO key); "external" = connects with an agent token (API/MCP/ACP). */
  runtime: z.enum(["hosted", "external"]),
  model: z.string().optional(),
  description: z.string().optional(),
  state: AgentState,
  /** Short live status shown under the composer, e.g. "Drafting release notes". */
  activity: z.string().optional(),
  /** The bot user of an app (apps.ts): the app's id. Bots are listed under Apps, not Agents. */
  appId: Id.optional(),
});
export type AgentProfile = z.infer<typeof AgentProfile>;

export const Member = z.object({
  userId: Id,
  workspaceId: Id,
  displayName: z.string(),
  fullName: z.string(),
  title: z.string().optional(),
  avatarUrl: z.string().optional(),
  role: Role,
  status: UserStatus.optional(),
  timezone: z.string().optional(),
  locale: Locale.optional(),
  /** Present only when the viewer may see it (own record, unrestricted viewers, or `can.seeEmails`). Never sent in realtime events. */
  email: z.string().optional(),
  kind: MemberKind,
  /** Present iff kind === "agent". The accountable human is `access.sponsorId` (required for agents and guests). */
  agent: AgentProfile.optional(),
  /** Scoped access (guests, agents). Absent ⇒ full access for `role`. */
  access: AccessPolicy.optional(),
  deactivated: z.boolean(),
  /** "she/her" (profile settings). */
  pronouns: z.string().optional(),
  /**
   * Present only for the member themself, or when they share it (`phoneVisible`) with an unrestricted viewer.
   * Never sent in realtime events.
   */
  phone: z.string().optional(),
  /** Own record only: the phone number is shown to the workspace (default: private). */
  phoneVisible: z.boolean().optional(),
  /**
   * Username, unique in the workspace (lowercase; core `logic/handles`): shown as "@handle", and `@handle` in a
   * message mentions this member. Assigned by the server when missing.
   */
  handle: z.string().optional(),
  /** Photo, initials on a tone, or emoji on a tone. A photo is also mirrored in `avatarUrl` (older clients). */
  avatar: MemberAvatar.optional(),
  /** Do not disturb (pause and quiet hours), so others see a moon while it's on. Absent ⇒ never paused. */
  dnd: MemberDnd.optional(),
});
export type Member = z.infer<typeof Member>;

export const ChannelKind = z.enum(["public", "private", "dm", "group_dm"]);
export type ChannelKind = z.infer<typeof ChannelKind>;

export const Channel = z.object({
  id: Id,
  workspaceId: Id,
  kind: ChannelKind,
  /** Display name. For DMs the server returns "" and clients derive the name from members. */
  name: z.string(),
  topic: z.string().optional(),
  purpose: z.string().optional(),
  createdBy: Id,
  createdAt: Timestamp,
  archived: z.boolean(),
  memberCount: z.number().int(),
  /** Seq of the newest top-level message (or 0). Used with membership.lastReadSeq for unread. */
  lastMessageSeq: z.number().int(),
  lastMessageAt: Timestamp.optional(),
  /** For dm / group_dm: the participant user ids. */
  dmUserIds: z.array(Id).optional(),
  /** True when any member belongs to another organization (guest) — clients show an "external" marker. */
  external: z.boolean().optional(),
});
export type Channel = z.infer<typeof Channel>;

export const NotifyLevel = z.enum(["default", "all", "mentions", "none"]);
export type NotifyLevel = z.infer<typeof NotifyLevel>;

export const ChannelMembership = z.object({
  channelId: Id,
  userId: Id,
  lastReadSeq: z.number().int(),
  /** Unread @-mentions (incl. DMs) since lastReadSeq — maintained by the server. */
  mentionCount: z.number().int(),
  notify: NotifyLevel,
  muted: z.boolean(),
  /** In my Starred sidebar section (starring takes it out of a folder). */
  starred: z.boolean(),
  /**
   * DMs / group DMs only: when I closed the conversation ("Close conversation", Slack). It stays out of my sidebar
   * while the channel's lastMessageAt is not after this, or until I open it again. I stay a member. Absent = open.
   */
  closedAt: Timestamp.optional(),
  joinedAt: Timestamp,
});
export type ChannelMembership = z.infer<typeof ChannelMembership>;

// ---------- My sidebar organization (per user, synced across my devices) ----------

/** A folder in one person's sidebar: holds their channels, DMs and docs items (pages, databases, boards). */
export const SidebarFolder = z.object({
  id: Id,
  name: z.string(),
  /** User-chosen label emoji (content); shown instead of `icon`. */
  emoji: z.string().optional(),
  /** Icon name from the shared icon font (packages/tokens/icons.json); absent = "folder". */
  icon: z.string().optional(),
  /** Order among my folders (fractional-indexing key, plain string order). */
  rank: z.string(),
  collapsed: z.boolean(),
});
export type SidebarFolder = z.infer<typeof SidebarFolder>;

export const SidebarItemKind = z.enum(["channel", "dm", "page"]);
export type SidebarItemKind = z.infer<typeof SidebarItemKind>;

/**
 * Where one of my sidebar items lives, when not in its default place: in one of my folders (`folderId`), and/or in an
 * order I chose (`rank`, fractional-indexing; absent = default order after the ranked ones). `page` = any docs item
 * (page, database, board): it keeps its place in the page tree (visibility, parent, permissions); a folder only
 * organizes my sidebar. Whether a conversation is starred lives on its membership.
 */
export const SidebarItem = z.object({
  kind: SidebarItemKind,
  id: Id,
  folderId: Id.optional(),
  rank: z.string().optional(),
});
export type SidebarItem = z.infer<typeof SidebarItem>;

// ---------- Rich text AST (server-parsed; clients only render) ----------

export const Mark = z.enum(["bold", "italic", "strike", "code"]);
export type Mark = z.infer<typeof Mark>;

export const Inline = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string(), marks: z.array(Mark).optional() }),
  z.object({ type: z.literal("link"), url: z.string(), text: z.string().optional() }),
  z.object({ type: z.literal("mention_user"), userId: Id }),
  z.object({ type: z.literal("mention_channel"), channelId: Id }),
  z.object({ type: z.literal("mention_broadcast"), range: z.enum(["here", "channel", "everyone"]) }),
  /** A user group (userGroups.ts): `@handle` or Slack's `<!subteam^ID>`. Notifies its members in the conversation. */
  z.object({ type: z.literal("mention_group"), groupId: Id }),
  z.object({ type: z.literal("emoji"), name: z.string(), unicode: z.string().optional() }),
]);
export type Inline = z.infer<typeof Inline>;

export type Block =
  | { type: "paragraph"; children: Inline[] }
  | { type: "code_block"; text: string; lang?: string }
  | { type: "quote"; children: Block[] }
  | { type: "list"; ordered: boolean; items: Block[][] };

export const Block: z.ZodType<Block> = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.object({ type: z.literal("paragraph"), children: z.array(Inline) }),
    z.object({ type: z.literal("code_block"), text: z.string(), lang: z.string().optional() }),
    z.object({ type: z.literal("quote"), children: z.array(Block) }),
    z.object({ type: z.literal("list"), ordered: z.boolean(), items: z.array(z.array(Block)) }),
  ]),
);

// ---------- Messages ----------

export const FileRef = z.object({
  id: Id,
  name: z.string(),
  mime: z.string(),
  size: z.number().int(),
  /** API-relative path of the content (`/v1/w/{ws}/files/{id}`): bearer token, or `?t=<file token>` (files.ts). */
  url: z.string(),
  /** Client-made thumbnail (images, videos), same auth as `url`. */
  thumbUrl: z.string().optional(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
});
export type FileRef = z.infer<typeof FileRef>;

export const Reaction = z.object({
  emoji: z.string(),
  count: z.number().int(),
  userIds: z.array(Id),
});
export type Reaction = z.infer<typeof Reaction>;

// ---------- Stickers & GIFs in messages (packs, library and GIF config: stickers.ts) ----------

/** A sticker as carried by a message: a snapshot taken when it was sent (later edits don't change old messages). */
export const StickerRef = z.object({
  id: Id,
  packId: Id,
  name: z.string(),
  /** API-relative image URL (same auth and media pipeline as `FileRef.url`); 512×512 WebP, or an animated original. */
  url: z.string(),
  mime: z.string(),
  width: z.number().int(),
  height: z.number().int(),
  /** Animated WebP/APNG/GIF (rendered with an animated decoder natively). */
  animated: z.boolean().optional(),
});
export type StickerRef = z.infer<typeof StickerRef>;

/** GIF search providers. KLIPY's terms: client-side requests, media hotlinked from its CDN, "Search KLIPY". */
export const GifProvider = z.enum(["klipy"]);
export type GifProvider = z.infer<typeof GifProvider>;

/** A GIF as carried by a message. Media stay on the provider's CDN (never re-hosted, per its terms). */
export const GifRef = z.object({
  provider: GifProvider,
  /** Provider id/slug (share and report pings). */
  id: z.string().min(1).max(200),
  title: z.string().max(300),
  /** Animated rendition on the provider CDN (https, provider host only). */
  url: z.string().max(2048),
  mime: z.string().max(100),
  width: z.number().int().positive().max(4096),
  height: z.number().int().positive().max(4096),
  /** Still frame on the same CDN (reduced motion, placeholders). */
  stillUrl: z.string().max(2048).optional(),
});
export type GifRef = z.infer<typeof GifRef>;

/** Messages posted by an integration (incoming webhook / app) rather than a member. */
export const AppAuthor = z.object({ appId: Id, name: z.string(), iconUrl: z.string().optional() });
export type AppAuthor = z.infer<typeof AppAuthor>;

/**
 * A link preview under a message (Slack's unfurl; core logic/unfurl, API src/unfurl): what the server read from the
 * page's OpenGraph / Twitter meta tags or the host's oEmbed. `imageUrl` points at the server's signed image proxy
 * (readers never load it from the site). The author can remove a preview; the workspace can turn previews off
 * (`Workspace.unfurlLinks`).
 */
export const LinkUnfurl = z.object({
  /** The link as written in the message (the preview's key). */
  url: z.string().max(2048),
  title: z.string().max(300).optional(),
  description: z.string().max(1000).optional(),
  /** "YouTube", "GitHub", or the host when the page names no site. */
  siteName: z.string().max(100).optional(),
  imageUrl: z.string().max(2048).optional(),
  imageWidth: z.number().int().optional(),
  imageHeight: z.number().int().optional(),
  /** oEmbed video / rich embeds (YouTube, Vimeo, Loom…): a play badge; YouTube / Vimeo / Loom play inline (core videoEmbedUrl). */
  video: z.boolean().optional(),
});
export type LinkUnfurl = z.infer<typeof LinkUnfurl>;

/**
 * A message shared into another conversation (Slack "Share message" / forward; messageExtras.ts): a snapshot of the
 * original when it was shared — its author, text and where it was posted. `channelId` / `messageId` open it in
 * context for people who can read that conversation.
 */
export const SharedMessage = z.object({
  messageId: Id,
  channelId: Id,
  threadRootId: Id.optional(),
  authorId: Id,
  text: z.string(),
  blocks: z.array(Block),
  createdAt: Timestamp,
});
export type SharedMessage = z.infer<typeof SharedMessage>;

/**
 * AI "TL;DR" of a thread (docs/features/thread-summaries.md), kept on its root message: 2–4 bullets written by the
 * model gateway's small model in the thread's language, covering the replies up to `upToMessageId`. Readable by
 * exactly the people who can read the thread. Clients show it under the root (core `logic/threadSummary`).
 */
export const ThreadSummary = z.object({
  bullets: z.array(z.string()).min(1).max(6),
  /** The language the model was asked to write in (the thread's, else the workspace default). */
  locale: Locale,
  /** The model id that wrote it. */
  model: z.string(),
  generatedAt: Timestamp,
  /** The newest message it covers (the root when there were no replies yet). */
  upToMessageId: Id,
  /** Replies it covers (the thread's replyCount when it was written). */
  replyCount: z.number().int().nonnegative(),
});
export type ThreadSummary = z.infer<typeof ThreadSummary>;

/**
 * A word of a caption and when it was said, in ms from the start of its phrase (`startedAt` on the caption or the
 * transcript line), so transcripts and recordings can jump to a word. On-device engines give these (Apple Speech with
 * Align, Desert Ant Voz); Whisper gives them for server captions.
 */
export const CaptionWord = z.object({
  text: z.string().min(1).max(100),
  start: z.number().int().nonnegative().max(10 * 60_000),
  end: z.number().int().nonnegative().max(10 * 60_000),
});
export type CaptionWord = z.infer<typeof CaptionWord>;

/** Where a caption was made: on the speaker's device (only text was sent) or by Bundle's server from audio clips. */
export const CaptionSource = z.enum(["device", "server"]);
export type CaptionSource = z.infer<typeof CaptionSource>;

/** One line of a huddle's transcript. `startedAt`, `words` and `source` are absent on lines from before word timings. */
export const HuddleTranscriptLine = z.object({
  userId: Id,
  name: z.string(),
  text: z.string(),
  ts: Timestamp,
  /** When the phrase began (server clock); word times count from here. */
  startedAt: Timestamp.optional(),
  words: z.array(CaptionWord).optional(),
  source: CaptionSource.optional(),
});
export type HuddleTranscriptLine = z.infer<typeof HuddleTranscriptLine>;

/** AI huddle notes (Slack's "Huddle notes"): written by the small model from the transcript after the huddle. */
export const HuddleNotes = z.object({
  summary: z.array(z.string()).max(8),
  actionItems: z.array(z.string()).max(20),
  decisions: z.array(z.string()).max(20),
  locale: Locale,
  model: z.string(),
  generatedAt: Timestamp,
});
export type HuddleNotes = z.infer<typeof HuddleNotes>;

/**
 * The huddle message in the conversation (Message.huddle): who was in it, how long, who missed it, the transcript when
 * captions were on, and the AI notes (`notes`, written after the huddle when the workspace allows AI; `notesPending` while the model
 * is writing them). Recordings are replies in its thread.
 */
export const HuddleRecap = z.object({
  huddleId: Id,
  startedAt: Timestamp,
  /** Absent while the huddle is still going. */
  endedAt: Timestamp.optional(),
  participantIds: z.array(Id),
  /**
   * People rung into the huddle (a DM call, or "Invite people") who never joined it, with who rang them (the latest
   * ring). Each of them sees Slack's "Missed huddle" line with Call back (core logic/huddle `missedHuddle`). Set when
   * the huddle ends; absent when nobody missed it.
   */
  missed: z.array(z.object({ userId: Id, callerId: Id })).optional(),
  transcript: z.array(HuddleTranscriptLine),
  notes: HuddleNotes.optional(),
  notesPending: z.boolean().optional(),
});
export type HuddleRecap = z.infer<typeof HuddleRecap>;

export const Message = z.object({
  id: Id,
  channelId: Id,
  /** Per-channel monotonically increasing sequence (top-level and replies share the sequence). */
  seq: z.number().int(),
  threadRootId: Id.optional(),
  authorId: Id,
  /** Original markdown-ish source text (for editing + search). */
  text: z.string(),
  blocks: z.array(Block),
  createdAt: Timestamp,
  editedAt: Timestamp.optional(),
  deleted: z.boolean(),
  reactions: z.array(Reaction),
  replyCount: z.number().int(),
  replyUserIds: z.array(Id),
  lastReplyAt: Timestamp.optional(),
  /** Thread reply that was also posted to the channel. */
  alsoSentToChannel: z.boolean().optional(),
  files: z.array(FileRef),
  /** Client-generated id echoed back for optimistic-send reconciliation. */
  clientId: z.string().optional(),
  /** When the client composed it (offline queue replay). Metadata only — ordering is always by server `seq`/`createdAt`. */
  clientCreatedAt: Timestamp.optional(),
  app: AppAuthor.optional(),
  /** Block Kit layout an app posted (blockKit.ts), shown under/instead of `text` (which stays the plain fallback). */
  appBlocks: z.array(KitBlock).optional(),
  /** A sticker message (no text, no files): rendered large without a bubble; tap → its pack (stickers.ts). */
  sticker: StickerRef.optional(),
  /** A GIF message (no text, no files), hotlinked from the provider with its attribution (stickers.ts). */
  gif: GifRef.optional(),
  /** Pinned to its conversation (messageExtras.ts): who pinned it and when. Absent = not pinned. */
  pinnedBy: Id.optional(),
  pinnedAt: Timestamp.optional(),
  /** This message shares (forwards) another one, shown as a quote under its text. */
  shared: SharedMessage.optional(),
  /** Link previews (Slack unfurls), in the order the links appear; filled in by the server shortly after posting. */
  unfurls: z.array(LinkUnfurl).optional(),
  /** Thread roots only: the AI TL;DR of the thread (absent until one was written, or when the workspace turned them off). */
  summary: ThreadSummary.optional(),
  /** The huddle message of a conversation (Slack's huddle post): attendees, transcript, AI notes. Recordings reply to it. */
  huddle: HuddleRecap.optional(),
});
export type Message = z.infer<typeof Message>;


export const Presence = z.enum(["active", "away"]);
export type Presence = z.infer<typeof Presence>;
